import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  { 
    id: "graph1", 
    title: "Evolución en el tiempo según categorías de pantallas", 
    menuTitle: "Categorías de pantallas", 
    text: `Según la encuesta Juventud y Bienestar (2024), el 75% de los adolescentes utiliza internet entre 0 y 2 horas diarias, una proporción que se ha mantenido 
          estable respecto a la medición anterior. En el caso de los videojuegos, se observa una tendencia a la baja en el uso intensivo: el grupo que juega 5 horas 
          o más se redujo del 19% al 16% (respecto al periodo 2021-2022), mientras que el uso moderado (0-2 horas) aumentó un 5%, situándose en un 65%. Por el contrario, 
          el consumo de contenido audiovisual y redes sociales muestra una tendencia opuesta hacia tiempos más prolongados, evidenciada por un aumento del 3% y 2% 
          respectivamente en los usuarios que dedican más de 5 horas a estas actividades.`, 
    link: "https://public.flourish.studio/visualisation/25637347/embed"
  },
  { 
    id: "graph2", 
    title: "Análisis por sexo según categorías de pantallas", 
    menuTitle: "\u2022 Sexo", 
    text: `Los datos sugieren que existen patrones de uso de pantallas diferenciados entre niñas y niños. Mientras que las primeras presentan un mayor uso de redes 
    sociales, los segundos dedican significativamente más tiempo a los videojuegos. La mayor diferencia se observa en el uso de videojuegos, donde el 24% de los niños 
    reporta un uso de más de 5 o 6 horas diarias, mientras que sólo un 7% de las niñas alcanza este nivel. En contraste, las niñas lideran el uso de redes sociales, 
    donde el 42% de ellas lo hace por periodos de 5-6 horas o más, en comparación con el 26% de los niños en la misma categoría.
    
  Tanto en el consumo de contenido audiovisual como en el uso de internet para otras actividades, se observa ligeramente un mayor uso por parte de las niñas. Esto se 
    explica porque presentan una mayor concentración en el rango de uso más elevado y, a su vez, una menor presencia en los rangos de menor tiempo de consumo en ambas 
    categorías.`, 
    link: "https://public.flourish.studio/visualisation/25636530/embed"
  },
  { 
    id: "graph3", 
    title: "Análisis por dependencia administrativa según categorías de pantallas", 
    menuTitle: "\u2022 Dependencia", 
    text: `De manera transversal se observa que aquellos estudiantes provenientes de colegios subvencionados presentan la mayor proporción de consumo de pantallas en 
    todas las categorías y rangos horarios. Particularmente, registran los porcentajes más altos en el rango de 5-6 o más horas diarias. 
    
  Los jóvenes de colegios públicos se ubican en segundo lugar en la mayoría de las categorías y rangos de consumo. En contraste, los estudiantes de colegios 
    particulares presentan, de manera consistente, los menores niveles de consumo en todas las categorías, con porcentajes considerablemente más bajos.`, 
    link: "https://public.flourish.studio/visualisation/25659303/embed"
  },
  { 
    id: "graph4", 
    title: "Análisis general de consumo total de pantallas", 
    menuTitle: "Consumo total", 
    text: `Mientras que en 2024 solo el 5% de los estudiantes de 2° medio en Chile cumplió con el uso de pantallas recomendado por expertos, la gran mayoría (86%) 
          supera las 5-6 horas diarias. Este indicador de alto consumo mostró un incremento del 1% en comparación con la medición de 2021-2022.`, 
    link: "https://public.flourish.studio/visualisation/25437227/embed"
  },
  { 
    id: "graph5", 
    title: "Análisis por sexo del consumo total de pantallas", 
    menuTitle: "\u2022 Sexo", 
    text: `No hay diferencias significativas por sexo. Tanto en niñas como en niños un 85% reporta un consumo de pantallas que supera las 5 o 6 horas diarias. Si bien, 
          como se observó anteriormente, el uso que le dan las niñas y niños a las pantallas varía por género, el tiempo que pasan en total frente a una pantalla tiende 
          a ser el mismo.`, 
    link: "https://public.flourish.studio/visualisation/25438365/embed"
  },
  { 
    id: "graph6", 
    title: "Análisis por dependencia administrativa del consumo total de pantallas", 
    menuTitle: "\u2022 Dependencia", 
    text: `Se pueden observar diferencias en la cantidad de horas de uso de pantallas según la dependencia administrativa del colegio, especialmente en la categoría de 
          mayor consumo.  Entre las niñas y niños de colegios tanto subvencionados como públicos, se concentran los porcentajes más elevados de consumo de pantallas en 
          la categoría de "5-6 o más horas", con un 87% y 85%, respectivamente. Mientras que las y los jóvenes pertenecientes a colegios particulares pagados presentan 
          un nivel levemente más bajo, con un 78%.`, 
    link: "https://public.flourish.studio/visualisation/25658955/embed"
  },
  { 
    id: "graph7", 
    title: "Análisis por región del consumo total de pantallas", 
    menuTitle: "\u2022 Región", 
    text: `Aunque en todas las regiones más del 80% de las y los estudiantes de 2° medio utiliza pantallas por más de 5 o 6 horas diarias, los datos presentan cierta 
          variabilidad. Antofagasta, Magallanes y Arica exhiben los índices más altos de consumo intensivo (llegando al 89%), mientras que Aysén, Ñuble y La Araucanía 
          muestran una prevalencia levemente menor. No obstante, las tendencias entre mediciones son divergentes: Aysén ha aumentado su exposición (el uso intensivo 
          creció un 4%), mientras que Atacama muestra una reducción del tiempo de conexión, disminuyendo en un 4% el grupo de mayor consumo.`, 
    link: "https://public.flourish.studio/visualisation/25437812/embed"
  }
];

const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const indentedGraphs = ["graph1", "graph4"];

  const handleMenuItemClick = (graphId) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh", display: "flex" }}>
          <Sider
            width={250}
            collapsible
            collapsed={collapsed}
            onCollapse={toggleCollapsed}
            style={{
              background: "#fff",
              height: "100vh",
            }}
            breakpoint="lg"
            collapsedWidth={0}
          >
            <div style={{ overflowY: "auto", height: "100%" }}>
              <Menu
                theme="dark"
                mode="vertical"
                selectedKeys={[selectedMenuItem]} 
                style={{ background: "#fff", height: "100%" }}
              >
                {graphs.map((graph) => (
                  <Menu.Item
                    key={graph.id}
                    onClick={() => handleMenuItemClick(graph.id)}
                    style={{
                      color: "black",
                      paddingLeft: indentedGraphs.includes(graph.id) ? "10px" : "30px",
                    }}
                  >
                    {graph.menuTitle}
                  </Menu.Item>
                ))}
              </Menu>
            </div>
          </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}> 
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "500px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;

